import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, Table, Button, Spinner, Tab, Tabs } from "react-bootstrap";
import { horizon } from "../api/horizon";
import { usePageMetadata } from "../context/PageMetadataContext";
import { useLanguage } from "../context/LanguageContext"; // Add this import
import CopyIcon from "../components/CopyIcon";
import OperationText from "../components/OperationText";
import { okx } from "../api/okx";
import { timelib } from "../utils/time"; // Import timelib for localized time formatting

const BlockDetail = () => {
  const { sequence } = useParams<{ sequence: string }>();
  const { t, language } = useLanguage(); // Add language hook
  const [blockData, setBlockData] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [operations, setOperations] = useState<any[]>([]);
  const [effects, setEffects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  //
  const [txNextLink, setTxNextLink] = useState("");
  const [txPrevLink, setTxPrevLink] = useState("");
  const [txPage, setTxPage] = useState(1);

  const [opNextLink, setOpNextLink] = useState("");
  const [opPrevLink, setOpPrevLink] = useState("");
  const [opPage, setOpPage] = useState(1);

  const [efNextLink, setEfNextLink] = useState("");
  const [efPrevLink, setEfPrevLink] = useState("");
  const [efPage, setEfPage] = useState(1);
  const [piPriceUSD, setPiPriceUSD] = useState(0.0);
  //
  const { setHeading, setTitle, setDescription } = usePageMetadata();

  // Format amount with language support
  const formatAmount = (amount: number): string => {
    return amount.toLocaleString(language, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 3 
    });
  };

  const fetchPiPrice = async () => {
    try {
      okx.getMarketData().then(marketData => {
        // Market Stats
        setPiPriceUSD(parseFloat(marketData.idxPx));
      })
    } catch (error) {
      console.error("Error fetching Pi price:", error);
    }
  };

  useEffect(() => {
    fetchPiPrice();
  }, []);

  useEffect(() => {
    // Use translations with template variables
    const translatedTitle = t('block.title').replace('{{sequence}}', sequence || '');
    const translatedHeading = t('block.heading').replace('{{sequence}}', sequence || '');
    const translatedDescription = t('block.description').replace('{{sequence}}', sequence || '');
    
    setTitle(translatedTitle);
    setHeading(translatedHeading);
    setDescription(translatedDescription);
    
    // Update metadata immediately
    document.title = translatedTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", translatedDescription);
  }, [sequence, setTitle, setDescription, setHeading, t, language]);

  // Fetch block detail from API
  const fetchBlockDetail = async () => {
    try {
      setLoading(true);
      if (sequence) {
        const response = await horizon.getLedgerBySequence(parseInt(sequence));
        setBlockData(response);
      } else {
        setBlockData(null);
      }
    } catch (error) {
      console.error("Error fetching block detail:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch paginated tab data
  const fetchTransactions = async (Link = "", page = 1) => {
    try {
      setTabLoading(true);
      const response = await horizon.getTransactions(sequence || "", Link);
      //if order asc, reverse the array, and next is prev and prev is next
      if (Link == "" || Link.indexOf("order=desc") > 0) {
        setTransactions(response._embedded.records);
        setTxNextLink(response._links.next.href);
        setTxPrevLink(response._links.prev.href);
        setTxPage(page);
      }
      else {
        setTransactions(response._embedded.records.reverse());
        setTxNextLink(response._links.prev.href);
        setTxPrevLink(response._links.next.href);
        setTxPage(page);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchOperations = async (Link = "", page = 1) => {
    try {
      setTabLoading(true);
      const response = await horizon.getOperations(sequence || "", Link);
      //if order asc, reverse the array, and next is prev and prev is next
      if (Link == "" || Link.indexOf("order=desc") > 0) {
        setOperations(response._embedded.records);
        setOpNextLink(response._links.next.href);
        setOpPrevLink(response._links.prev.href);
        setOpPage(page);
      }
      else {
        setOperations(response._embedded.records.reverse());
        setOpNextLink(response._links.prev.href);
        setOpPrevLink(response._links.next.href);
        setOpPage(page);
      }
    } catch (error) {
      console.error("Error fetching operations:", error);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchEffects = async (Link = "", page = 1) => {
    try {
      setTabLoading(true);
      const response = await horizon.getEffects(sequence || "", Link);
      //if order asc, reverse the array, and next is prev and prev is next
      if (Link == "" || Link.indexOf("order=desc") > 0) {
        setEffects(response._embedded.records);
        setEfNextLink(response._links.next.href);
        setEfPrevLink(response._links.prev.href);
        setEfPage(page);
      }
      else {
        setEffects(response._embedded.records.reverse());
        setEfNextLink(response._links.prev.href);
        setEfPrevLink(response._links.next.href);
        setEfPage(page);
      }
    } catch (error) {
      console.error("Error fetching effects:", error);
    } finally {
      setTabLoading(false);
    }
  };

  // Fetch transactions, operations, effects
  const fetchTabData = async () => {
    try {
      setTabLoading(true);
      if (sequence) {
        fetchTransactions("", 1);
        fetchOperations("", 1);
        fetchEffects("", 1);
      }
    } catch (error) {
      console.error("Error fetching tab data:", error);
    } finally {
      setTabLoading(false);
    }
  };

  useEffect(() => {
    fetchBlockDetail();
    fetchTabData();
  }, [sequence]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p>{t('block.loading')}</p>
      </div>
    );
  }

  if (!blockData) {
    return <p className="text-center text-danger">{t('block.not_found')}</p>;
  }

  return (
    <div className="white-zone">
      <h2 className="mb-4">{t('block.heading').replace('{{sequence}}', blockData.sequence.toString())}</h2>

      {/* Block Information */}
      <h5>{t('block.information')}</h5>
      <Table hover responsive borderless>
        <tbody>
          <tr>
            <th>{t('block.hash')}</th>
            <td>{blockData.hash} {" "}<CopyIcon textToCopy={blockData.hash} /></td>
          </tr>
          <tr>
            <th>{t('block.previous_hash')}</th>
            <td>
              <Link className="text-decoration-none" to={`/block/${blockData.sequence - 1}`}>
                {blockData.prev_hash}
              </Link>{" "}
              <CopyIcon textToCopy={blockData.prev_hash} />
            </td>
          </tr>
          <tr>
            <th>{t('block.sequence')}</th>
            <td>{blockData.sequence}</td>
          </tr>
          <tr>
            <th>{t('block.time')}</th>
            <td>{timelib.timeAgo(blockData.closed_at, language)}</td>
          </tr>
          <tr>
            <th>{t('block.total_coins')}</th>
            <td>{formatAmount(parseFloat(blockData.total_coins))} π</td>
          </tr>
          <tr>
            <th>{t('block.fee_pool')}</th>
            <td>{formatAmount(parseFloat(blockData.fee_pool))} π</td>
          </tr>
          <tr>
            <th>{t('block.base_fee')}</th>
            <td>{formatAmount(blockData.base_fee_in_stroops / 10000000)} π</td>
          </tr>
          <tr>
            <th>{t('block.base_reserve')}</th>
            <td>{formatAmount(blockData.base_reserve_in_stroops / 10000000)} π</td>
          </tr>
          <tr>
            <th>{t('block.protocol_version')}</th>
            <td>{blockData.protocol_version}</td>
          </tr>
        </tbody>
      </Table>

      {/* Tabs for Transactions, Operations, Effects */}
      <Tabs defaultActiveKey="transactions" className="mt-4">
        <Tab eventKey="transactions" title={t('block.tabs.transactions')}>
          {tabLoading ? (
            <Spinner animation="border" variant="primary" className="mt-3" />
          ) : (
            <>
              <Table hover responsive className="mt-3">
                <thead>
                  <tr>
                    <th>{t('block.tx_hash')}</th>
                    <th>{t('operations.source_account')}</th>
                    <th>{t('block.operation_count')}</th>
                    <th>{t('block.fee')}</th>
                    <th>{t('block.created_at')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id}>
                      <td>
                        <Link to={`/tx/${tx.hash}`} className="text-decoration-none">
                          {tx.hash.slice(0, 16)}...
                        </Link>
                        {" "}
                        <CopyIcon textToCopy={tx.hash} />
                      </td>
                      <td>
                        <Link to={`/account/${tx.source_account}`} className="text-decoration-none">
                          {tx.source_account.slice(0, 8)}...{tx.source_account.slice(-8)}
                        </Link>
                        {" "}
                        <CopyIcon textToCopy={tx.source_account} />
                      </td>
                      <td>{tx.operation_count}</td>
                      <td>{formatAmount(tx.fee_charged / 10_000_000)} π</td>
                      <td>{timelib.timeAgo(tx.created_at, language)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center mt-4">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => fetchTransactions(txPrevLink, txPage - 1)}
                  disabled={!txPrevLink || tabLoading || txPage === 1}
                >
                  {t('pagination.previous')}
                </Button>
                <span className="mx-3">{t('pagination.page')} {txPage}</span>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => fetchTransactions(txNextLink, txPage + 1)}
                  disabled={!txNextLink || tabLoading}
                >
                  {t('pagination.next')}
                </Button>
              </div>
            </>
          )}
        </Tab>

        <Tab eventKey="operations" title={t('block.tabs.operations')}>
          {tabLoading ? (
            <Spinner animation="border" variant="primary" className="mt-3" />
          ) : (
            <>
              <Table hover responsive className="mt-3">
                <thead>
                  <tr>
                    <th>{t('operations.source_account')}</th>
                    <th>{t('operations.operation')}</th>
                    <th>{t('operations.amount')}</th>
                    <th>{t('operations.tx')}</th>
                    <th>{t('operations.time')}</th>
                  </tr>
                </thead>
                <tbody>
                  {operations.map((op) => (
                    <tr key={op.id}>
                      <td>
                        <Link to={`/account/${op.source_account}`} className="text-decoration-none">
                          {op.source_account.slice(0, 8)}...{op.source_account.slice(-8)}
                        </Link>
                        {" "}
                        <CopyIcon textToCopy={op.source_account} />
                      </td>
                      <td><OperationText op={op} PiPrice={piPriceUSD} /></td>
                      <td style={{ fontWeight: "500" }}>
                        {op.amount ? formatAmount(parseFloat(op.amount)) : ""}
                      </td>
                      <td>
                        <Link to={`/tx/${op.transaction_hash}`} className="text-decoration-none">
                          {op.transaction_hash.slice(0, 16)}...
                        </Link>
                        {" "}
                        <CopyIcon textToCopy={op.transaction_hash} />
                      </td>
                      <td>{timelib.timeAgo(op.created_at, language)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center mt-4">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => fetchOperations(opPrevLink, opPage - 1)}
                  disabled={!opPrevLink || tabLoading || opPage === 1}
                >
                  {t('pagination.previous')}
                </Button>
                <span className="mx-3">{t('pagination.page')} {opPage}</span>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => fetchOperations(opNextLink, opPage + 1)}
                  disabled={!opNextLink || tabLoading}
                >
                  {t('pagination.next')}
                </Button>
              </div>
            </>
          )}
        </Tab>

        <Tab eventKey="effects" title={t('block.tabs.effects')}>
          {tabLoading ? (
            <Spinner animation="border" variant="primary" className="mt-3" />
          ) : (
            <>
              <Table hover responsive className="mt-3">
                <thead>
                  <tr>
                    <th>{t('account.account')}</th>
                    <th>{t('account.effect')}</th>
                    <th>{t('operations.amount')}</th>
                    <th>{t('operations.time')}</th>
                  </tr>
                </thead>
                <tbody>
                  {effects.map((effect) => (
                    <tr key={effect.id}>
                      <td>
                        <Link to={`/account/${effect.account}`} className="text-decoration-none">
                          {effect.account.slice(0, 8)}...{effect.account.slice(-8)}
                        </Link>
                        {" "}
                        <CopyIcon textToCopy={effect.account} />
                      </td>
                      <td>{t(`effects.${effect.type}`) || effect.type.split("_").join(" ")}</td>
                      <td style={{ fontWeight: "500" }}>
                        <span className={
                          effect.type == "account_credited" ? "text-success" : 
                          effect.type == "account_debited" ? "text-danger" : "text-muted"
                        }>
                          {effect.type == "account_debited" ? "-" : effect.type == "account_credited" ? "+" : ""}
                          {effect.amount ? formatAmount(parseFloat(effect.amount)) : ""}
                        </span>
                      </td>
                      <td>{timelib.timeAgo(effect.created_at, language)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center mt-4">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => fetchEffects(efPrevLink, efPage - 1)}
                  disabled={!efPrevLink || tabLoading || efPage === 1}
                >
                  {t('pagination.previous')}
                </Button>
                <span className="mx-3">{t('pagination.page')} {efPage}</span>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => fetchEffects(efNextLink, efPage + 1)}
                  disabled={!efNextLink || tabLoading}
                >
                  {t('pagination.next')}
                </Button>
              </div>
            </>
          )}
        </Tab>
      </Tabs>
    </div>
  );
};

export default BlockDetail;
